// Entitlements API fns — carved verbatim from `src/api/scribeAPIV2.ts`
// (which now re-exports from here). See organization.ts for the transport
// registry rationale (E5, ARCFE-805).

import { getScribeHttpClients } from '@heidi/api/client';
import { type components } from '@heidi/schema/ml-scribe';
import { type AccessEntitlementsResponse } from '../../contracts';
import { type WorkspaceHttpClient } from '../config';

type AccessEntitlementView = components['schemas']['AccessEntitlementView'];
type AssignAccessEntitlementRequest =
  components['schemas']['AssignAccessEntitlementRequest'];
type BulkAssignAccessEntitlementRequest =
  components['schemas']['BulkAssignAccessEntitlementRequest'];
type BulkAssignUserEntitlementsResponse =
  components['schemas']['BulkAssignUserEntitlementsResponse'];
type BulkDeleteUserAccessEntitlementRequest =
  components['schemas']['BulkDeleteUserAccessEntitlementRequest'];
type BulkDeleteUserEntitlementsResponse =
  components['schemas']['BulkDeleteUserEntitlementsResponse'];
type DeleteAccessEntitlementResponse =
  components['schemas']['DeleteAccessEntitlementResponse'];
type DeleteUserAccessEntitlementRequest =
  components['schemas']['DeleteUserAccessEntitlementRequest'];
type TeamLevelAccessEntitlementsResponse =
  components['schemas']['TeamLevelAccessEntitlementsResponse'];
type TeamMemberAccessEntitlementsListResponse =
  components['schemas']['TeamMemberAccessEntitlementsListResponse'];

const http = (): WorkspaceHttpClient => getScribeHttpClients().scribeHttpV2;

export const getEntitlements = () => {
  return http().get<AccessEntitlementsResponse>('/access-entitlements');
};

// `mintCodingCaseAccessContext` TRANSFERRED to `@heidi/coding/data` (the SCC
// inversion, ARCFE-986) — it was the design doc's named example of a
// disguised cross-domain command living in this file.

// ---------------------------------------------------------------------------
// Team-admin entitlement administration (EN3, ARCFE-997)
//
// The 8 `access-entitlements/*` admin endpoints, carved verbatim from the v2
// hub — the last api residue holding this family's `api` dimension at amber
// since EN1 measured it. The hub keeps name-intact re-exports, so all 18
// consumer files (6 mutations, 2 queries, their suites) compile unchanged.
//
// Two frozen oddities move AS-IS and must not be "tidied":
//   - the two `PUT` create-or-update writes (GRO-3640): a POST 409s when the
//     backfill already wrote a row, so assignment is idempotent by verb.
//   - `DELETE` with a request BODY (`{ data: body }`) on four fns — axios's
//     shape for it; a body-less delete would drop the payload silently.
// ---------------------------------------------------------------------------

export const getTeamAccessEntitlements = (teamId: string) => {
  return http().get<TeamLevelAccessEntitlementsResponse>(
    `/access-entitlements/team/${teamId}`
  );
};

export const assignTeamEntitlementAsTeamAdmin = (
  teamId: string,
  body: AssignAccessEntitlementRequest
) => {
  // PUT create-or-update — avoids 409 when backfill already wrote a row (GRO-3640)
  return http().put<AccessEntitlementView>(`/access-entitlements/team/${teamId}`, body);
};

export const deleteTeamEntitlementAsTeamAdmin = (
  teamId: string,
  body: DeleteUserAccessEntitlementRequest
) => {
  return http().delete<DeleteAccessEntitlementResponse>(
    `/access-entitlements/team/${teamId}`,
    { data: body }
  );
};

export const getTeamMemberAccessEntitlements = (teamId: string) => {
  return http().get<TeamMemberAccessEntitlementsListResponse>(
    `/access-entitlements/team/${teamId}/members`
  );
};

export const assignUserEntitlementAsTeamAdmin = (
  userRefId: string,
  body: AssignAccessEntitlementRequest
) => {
  // PUT create-or-update — avoids 409 when backfill already wrote a row (GRO-3640)
  return http().put<AccessEntitlementView>(
    `/access-entitlements/users/${userRefId}`,
    body
  );
};

export const bulkAssignUserEntitlementsAsTeamAdmin = (
  body: BulkAssignAccessEntitlementRequest
) => {
  return http().post<BulkAssignUserEntitlementsResponse>(
    `/access-entitlements/users/batch`,
    body
  );
};

export const bulkDeleteUserEntitlementsAsTeamAdmin = (
  body: BulkDeleteUserAccessEntitlementRequest
) => {
  return http().delete<BulkDeleteUserEntitlementsResponse>(
    `/access-entitlements/users/batch`,
    { data: body }
  );
};

export const deleteUserEntitlementAsTeamAdmin = (
  userRefId: string,
  body: DeleteUserAccessEntitlementRequest
) => {
  return http().delete<DeleteAccessEntitlementResponse>(
    `/access-entitlements/users/${userRefId}`,
    { data: body }
  );
};
